import { Link } from "react-router-dom";
import { ArrowRight, Phone, Sparkles, Star } from "lucide-react";
import { CONTACT } from "@/lib/data";

/**
 * ServicesHero, postcard-style hero showcasing the brand image.
 * Layout : left column = brief overline + 3 trust badges + 2 CTAs.
 *          right column = the postcard photo, slightly tilted with stamp + tape.
 */
const ServicesHero = () => (
  <section
    className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cream overflow-hidden"
    data-testid="services-hero"
  >
    {/* Decorative grain orbs */}
    <div
      aria-hidden="true"
      className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-50"
      style={{
        background:
          "radial-gradient(circle, rgba(197,138,63,0.16) 0%, transparent 65%)",
      }}
    />
    <div
      aria-hidden="true"
      className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none opacity-50"
      style={{
        background:
          "radial-gradient(circle, rgba(15,44,79,0.10) 0%, transparent 65%)",
      }}
    />

    <div className="container-tm relative">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* === LEFT, short brief === */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <p className="overline mb-5">Nos services</p>
          <h1 className="font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mb-6">
            Tout est inclus.
            <br />
            <em className="text-gold">Vous profitez.</em>
          </h1>
          <p className="font-sans text-ink/70 text-base md:text-lg leading-relaxed mb-9 max-w-md">
            De la création d'annonce à la maintenance, en passant par le ménage
            hôtelier, le pricing dynamique et l'accueil voyageur 7j/7. Une
            commission unique de <strong className="text-ink">15 % TTC</strong>.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 mb-9">
            <a
              href="#why-choose"
              data-testid="services-hero-cta-primary"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("why-choose");
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 90;
                  window.scrollTo({ top, behavior: "smooth" });
                }
              }}
              className="inline-flex items-center justify-center gap-2 bg-ink text-cream font-semibold px-7 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm"
            >
              Découvrir notre offre <ArrowRight size={15} />
            </a>
            <a
              href={CONTACT.phoneHref}
              data-testid="services-hero-cta-phone"
              className="inline-flex items-center gap-2 border border-ink/25 text-ink px-6 py-3.5 rounded-full hover:border-gold hover:text-gold transition-colors duration-300 text-sm"
            >
              <Phone size={14} strokeWidth={2} />
              {CONTACT.phone}
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-xs text-ink/55">
            <span className="inline-flex items-center gap-2">
              <Star size={13} fill="currentColor" strokeWidth={0} className="text-gold" />
              Note Airbnb 4,92 / 5
            </span>
            <span className="inline-flex items-center gap-2">
              <Sparkles size={13} fill="currentColor" strokeWidth={0} className="text-gold" />
              70 logements pas plus
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              Équipes 100 % bretonnes
            </span>
          </div>
        </div>

        {/* === RIGHT, postcard photo === */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="relative max-w-xl mx-auto lg:max-w-none">
            {/* Soft shadow / glow */}
            <div
              aria-hidden="true"
              className="absolute -inset-4 rounded-[28px] bg-gold/15 blur-2xl pointer-events-none"
            />

            {/* Postcard wrapper (tilted) */}
            <div
              className="relative rounded-[18px] overflow-hidden shadow-soft border border-cream bg-white"
              style={{
                transform: "rotate(-2.5deg)",
              }}
              data-testid="services-hero-postcard"
            >
              {/* Decorative tape, top-left */}
              <div
                aria-hidden="true"
                className="absolute -top-3 left-7 z-20 w-24 h-7 bg-gold/30 backdrop-blur-sm rounded-sm border border-gold/40"
                style={{
                  transform: "rotate(-12deg)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              />
              {/* Decorative tape, bottom-right */}
              <div
                aria-hidden="true"
                className="absolute -bottom-3 right-10 z-20 w-20 h-7 bg-sage/30 backdrop-blur-sm rounded-sm border border-sage/40"
                style={{
                  transform: "rotate(7deg)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              />

              {/* The branded image */}
              <img
                src="/images/services-hero-postcard.jpg"
                alt="Membre de l'équipe Terre et Mer Conciergerie tenant un porte-clés en bois Terre et Mer, avec les 3 valeurs : votre bien prêt à accueillir, zéro contrainte, confiance et sérénité"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="block w-full h-auto"
              />
            </div>

            {/* Caption, handwritten style */}
            <p
              className="absolute -bottom-7 left-4 sm:left-10 font-serif italic text-ink/55 text-sm md:text-base"
              style={{ transform: "rotate(-1deg)" }}
            >
              « Vous profitez, on s'occupe de tout. »
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default ServicesHero;
